/** Build + match the entity name index used by manuscript hover peek (#73). */

import { isJust, just, nothing, matchMaybe, type Maybe } from "../lib/maybe";
import {
  entityDisplayName,
  findOccurrenceNames,
  findOccurrencesInDoc,
} from "./entityOccurrences";
import { stripEntityFrontmatterBody } from "../lib/entityBody";
import { projectionPeekMarkdown, scanTextOf } from "../lib/zwdoc/scanText";
import type {
  EntityArcMoment,
  EntityFrontmatter,
} from "../lib/entityFrontmatter";
import { assertNever } from "../lib/assertNever";
import { joinProjectPath } from "../lib/runtime";
import { listProjectDatabaseEntries } from "../lib/tauri/database";
import { readProjectFile } from "../lib/tauri/core";
import { PROJECT_DATABASE_DIR } from "../lib/zwConfig";

/** One Database entry as known to the peek layer. */
export type EntityPeekEntry = {
  /** Basename under `Database/` (e.g. `Elena.md`). */
  fileName: string;
  /** Canonical display name (H1, else file stem). */
  displayName: string;
  aka: string[];
  plural: Maybe<string>;
  adjective: Maybe<string>;
  type: Maybe<string>;
  summary: Maybe<string>;
  image: Maybe<string>;
  /**
   * Markdown body with frontmatter stripped (`.zwdoc`: escaped plain text,
   * headings kept) — used when summary is absent.
   */
  body: string;
  color: Maybe<string>;
  /** Arc moments for #66 character peek enrichment. */
  arc: EntityArcMoment[];
};

export type EntityPeekMatch = {
  from: number;
  to: number;
  entry: EntityPeekEntry;
};

/**
 * Where entity underlines may appear. Start with scenes only; widen later
 * without changing the decoration/hover plugin.
 */
export type EntityPeekScope = "scenes";

export type EntityPeekScopeContext = {
  /** Present only for `Chapters/<folder>/<scene>`. */
  activeSceneInfo: Maybe<unknown>;
  editingSidecar: boolean;
};

/** Gate underlines for the active editor surface. Expand when scope grows. */
export function entityPeekScopeAllows(
  scope: EntityPeekScope,
  ctx: EntityPeekScopeContext,
): boolean {
  switch (scope) {
    case "scenes":
      return isJust(ctx.activeSceneInfo) && ctx.editingSidecar === false;
    default: {
      return assertNever(scope);
    }
  }
}

/** Prefer summary; fall back to body. Both are optional. */
export function entityPeekProse(entry: EntityPeekEntry): string {
  return matchMaybe(entry.summary, {
    nothing: () => entry.body.trim(),
    just: (summary) => {
      const trimmed = summary.trim();
      return trimmed.length > 0 ? trimmed : entry.body.trim();
    },
  });
}

function peekBody(raw: string): string {
  const scan = scanTextOf(raw);
  return scan.kind === "zwdoc"
    ? projectionPeekMarkdown(scan.projection)
    : stripEntityFrontmatterBody(scan.text);
}

function entryFromParts(
  fileName: string,
  raw: string,
  fields: EntityFrontmatter,
): EntityPeekEntry {
  return {
    fileName,
    displayName: entityDisplayName(raw, fileName),
    aka: fields.aka,
    plural: fields.plural,
    adjective: fields.adjective,
    type: fields.type,
    summary: fields.summary,
    image: fields.image,
    body: peekBody(raw),
    color: fields.color,
    arc: fields.arc,
  };
}

/**
 * Full peek index for every non-map Database entry.
 * `live` overrides on-disk content when that entry is open in the editor.
 */
export async function collectEntityPeekIndex(
  rootPath: string,
  live: Maybe<{
    fileName: string;
    doc: string;
    fields: Maybe<EntityFrontmatter>;
  }>,
): Promise<EntityPeekEntry[]> {
  const summaries = await listProjectDatabaseEntries(rootPath);
  const entries = await Promise.all(
    summaries
      .filter((s) => s.isMap === false)
      .map(async ({ name: fileName, fields: diskFields }) => {
        const liveEntry =
          isJust(live) && live.value.fileName === fileName ? live : nothing;
        const fields =
          isJust(liveEntry) && isJust(liveEntry.value.fields)
            ? liveEntry.value.fields.value
            : diskFields;
        let raw: string;
        if (isJust(liveEntry)) {
          raw = liveEntry.value.doc;
        } else {
          try {
            raw = await readProjectFile(
              joinProjectPath(rootPath, `${PROJECT_DATABASE_DIR}/${fileName}`),
            );
          } catch {
            raw = "";
          }
        }
        return entryFromParts(fileName, raw, fields);
      }),
  );
  return entries.filter(
    (e) =>
      findOccurrenceNames({
        name: e.displayName,
        aka: e.aka,
        plural: e.plural,
        adjective: e.adjective,
      }).length > 0,
  );
}

function rangesOverlap(
  a: { from: number; to: number },
  b: { from: number; to: number },
): boolean {
  return a.from < b.to && b.from < a.to;
}

/**
 * Case-sensitive whole-word matches across all entries, skipping fenced code
 * in legacy bodies (`skipFences` false for plain-text projections).
 * Longer needles win when spans overlap (e.g. "Elena Varga" before "Elena").
 */
export function findEntityPeekMatches(
  raw: string,
  entries: readonly EntityPeekEntry[],
  skipFences = true,
): EntityPeekMatch[] {
  const candidates: EntityPeekMatch[] = [];
  for (const entry of entries) {
    for (const hit of findOccurrencesInDoc(
      raw,
      {
        name: entry.displayName,
        aka: entry.aka,
        plural: entry.plural,
        adjective: entry.adjective,
      },
      skipFences,
    )) {
      candidates.push({ from: hit.from, to: hit.to, entry });
    }
  }

  candidates.sort((a, b) => {
    const byLength = b.to - b.from - (a.to - a.from);
    return byLength === 0 ? a.from - b.from : byLength;
  });

  const taken: EntityPeekMatch[] = [];
  for (const candidate of candidates) {
    if (taken.some((t) => rangesOverlap(t, candidate))) continue;
    taken.push(candidate);
  }
  return taken.sort((a, b) => a.from - b.from);
}

/** Match covering `pos` (or adjacent when `side` points into a mark). */
export function entityPeekMatchAt<M extends EntityPeekMatch>(
  matches: readonly M[],
  pos: number,
  side: -1 | 1,
): Maybe<M> {
  for (const match of matches) {
    if (pos > match.from && pos < match.to) return just(match);
    if (side > 0 && pos === match.from) return just(match);
    if (side < 0 && pos === match.to) return just(match);
  }
  return nothing;
}
